import nBossConnection from 'modules/base/service/nBossConnection';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { logoutUser, getUserData } from 'app/auth/store/userSlice';
import { loginNextStep } from 'app/auth/store/loginSlice';
import { showMessage } from 'app/store/fuse/messageSlice';
import AccessManagementService from 'app/admin/user/service/AccessManagementService';
import UserLoginService from 'app/admin/user/service/UserLoginService';
import { useTranslation } from 'react-i18next';

const STEP = {
	LOGGED_IN: 'loggedIn',
	CHOOSE_GROUP: 'chooseGroup',
	CHOOSE_ROLE: 'chooseRole',
	NO_GROUP: 'noGroup',
	NO_ROLE: 'noRole'
};

const isStepSuccess = result => {
	if (!result) return false;
	if (typeof result.lastStepSuccessful === 'boolean') return result.lastStepSuccessful;
	if (typeof result.successful === 'boolean') return result.successful;
	return true;
};

const getNextStep = result => (result && result.nextStep != null ? String(result.nextStep) : null);

const getRoleValue = value => {
	if (value == null) return null;
	if (typeof value === 'string') return value;
	if (typeof value === 'object') return value.key ?? value.name ?? value.title ?? null;
	return String(value);
};

const formatRole = role => {
	const value = String(getRoleValue(role) || '');
	return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
};

const stepPayload = (result, nextStep) => ({
	nextStep,
	meta: {
		...(result?.meta ?? {}),
		groups: result?.userGroups ?? result?.groups ?? result?.meta?.groups ?? result?.client?.userGroups ?? null,
		roles: result?.roles ?? result?.meta?.roles ?? result?.client?.roles ?? null,
		userGroupId: result?.userGroupId ?? result?.groupId ?? result?.meta?.userGroupId ?? result?.meta?.groupId ?? null,
		role: result?.role ?? result?.meta?.role ?? null
	},
	client: result?.client ?? null
});

function UserMenu() {
	const dispatch = useDispatch();
	const history = useHistory();
	const { t } = useTranslation('auth');
	const user = useSelector(({ auth }) => auth.user);
	const login = useSelector(({ auth }) => auth.login);
	const [userMenu, setUserMenu] = useState(null);
	const [switching, setSwitching] = useState(false);

	const userMenuClick = event => {
		setUserMenu(event.currentTarget);
	};

	const userMenuClose = useCallback(() => {
		setUserMenu(null);
	}, []);

	const name = user?.data?.name || '';
	const isAuthenticated = Array.isArray(user?.role) && user.role.length > 0;
	const groups = user?.data?.userGroups ?? user?.userGroups ?? login?.meta?.groups ?? login?.client?.userGroups ?? [];
	const activeGroupId = user?.data?.activeUserGroupId ?? user?.data?.userGroupId ?? user?.activeUserGroupId ?? user?.userGroupId ?? login?.meta?.userGroupId ?? login?.meta?.groupId ?? login?.client?.userGroupId ?? login?.client?.activeUserGroupId ?? null;
	const hasGroups = Array.isArray(groups) && groups.length > 0;

	const roles = Array.isArray(user?.data?.roles) ? user.data.roles : (Array.isArray(user?.roles) ? user.roles : (Array.isArray(login?.meta?.roles) ? login.meta.roles : (Array.isArray(login?.client?.roles) ? login.client.roles : [])));
	const currentRole = user?.data?.role ?? user?.accessRole ?? login?.meta?.role ?? null;
	const hasRoles = Array.isArray(roles) && roles.length > 0;
	const roleListLine = user?.roleList ? user.roleList.map(role => role.title).join(', ') : '';
	const rolesLine = hasRoles ? roles.filter(Boolean).map(formatRole).join(', ') : roleListLine;

	const handleSwitchResult = useCallback(async (result, failMessage) => {
		const step = getNextStep(result);

		if (result === true || result?.isLoggedIn === true || step === STEP.LOGGED_IN) {
			await dispatch(getUserData());
			window.location.reload();
			return;
		}

		if (step) {
			dispatch(loginNextStep(stepPayload(result, step)));
			userMenuClose();
			history.push('/login');
			return;
		}

		if (!isStepSuccess(result)) {
			dispatch(showMessage({ message: failMessage }));
			return;
		}

		await dispatch(getUserData());
		window.location.reload();
	}, [dispatch, history, userMenuClose]);

	const handleSwitchGroup = useCallback(async groupId => {
		if (switching) return;
		setSwitching(true);

		try {
			const result = await AccessManagementService.changeUserGroup(groupId);
			await handleSwitchResult(result, 'Group switch failed. Please try again.');
		} catch (e) {
			dispatch(showMessage({ message: 'Network error. Please try again.' }));
		} finally {
			setSwitching(false);
		}
	}, [dispatch, handleSwitchResult, switching]);

	const handleSwitchRole = useCallback(async role => {
		if (switching) return;
		setSwitching(true);

		try {
			const roleValue = getRoleValue(role);
			const result = UserLoginService.changeRole
				? await UserLoginService.changeRole(roleValue)
				: await AccessManagementService.changeRole(roleValue);

			await handleSwitchResult(result, 'Role switch failed. Please try again.');
		} catch (e) {
			dispatch(showMessage({ message: 'Network error. Please try again.' }));
		} finally {
			setSwitching(false);
		}
	}, [dispatch, handleSwitchResult, switching]);

	return (
		<>
			<Button className="min-h-40 min-w-40 px-0 md:px-16 py-0 md:py-6" onClick={userMenuClick}>
				<div className="hidden md:flex flex-col mx-4 items-end">
					<Typography component="span" className="font-semibold flex">
						{name}
					</Typography>
					<Typography className="text-11 font-medium capitalize" color="textSecondary">
						{rolesLine}
					</Typography>
				</div>

				{user?.data?.profilePicture ? (
					<Avatar className="md:mx-4" alt={`${name} photo`} src={`${nBossConnection.baseUrl}fileRepository/getFile?key=${user.data.profilePicture.key}`} />
				) : (
					<Avatar className="md:mx-4">{name}</Avatar>
				)}
			</Button>

			<Popover
				open={Boolean(userMenu)}
				anchorEl={userMenu}
				onClose={userMenuClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'center'
				}}
				classes={{
					paper: 'py-8'
				}}
			>
				{!isAuthenticated ? (
					<>
						<MenuItem component={Link} to="/login" role="button">
							<ListItemIcon className="min-w-40">
								<Icon>lock</Icon>
							</ListItemIcon>
							<ListItemText primary={t('LOGIN')} />
						</MenuItem>
						<MenuItem component={Link} to="/register" role="button">
							<ListItemIcon className="min-w-40">
								<Icon>person_add</Icon>
							</ListItemIcon>
							<ListItemText primary={t('REGISTER')} />
						</MenuItem>
					</>
				) : (
					<>
						<MenuItem component={Link} to={`/userprofile/${user?.data?.id}`} onClick={userMenuClose} role="button">
							<ListItemIcon className="min-w-40">
								<Icon>account_circle</Icon>
							</ListItemIcon>
							<ListItemText primary={t('MY_PROFILE')} />
						</MenuItem>

						{hasRoles && (
							<>
								<Divider className="my-4" />
								<Typography className="px-16 py-6 text-12 opacity-60 uppercase">Switch role</Typography>
								{roles.map(role => {
									const roleValue = getRoleValue(role);
									const isActive = currentRole != null && String(currentRole) === String(roleValue);

									return (
										<MenuItem key={String(roleValue)} disabled={switching || isActive} onClick={() => handleSwitchRole(role)}>
											<ListItemIcon className="min-w-32">
												<Icon>{isActive ? 'check_circle' : 'verified_user'}</Icon>
											</ListItemIcon>
											<ListItemText primary={formatRole(role)} />
										</MenuItem>
									);
								})}
							</>
						)}

						{hasGroups && (
							<>
								<Divider className="my-4" />
								<Typography className="px-16 py-6 text-12 opacity-60 uppercase">Switch group</Typography>
								{groups.map(group => {
									const groupId = group?.id ?? group?.userGroupId ?? group;
									const isActive = activeGroupId != null && String(activeGroupId) === String(groupId);

									return (
										<MenuItem key={String(groupId)} disabled={switching || isActive} onClick={() => handleSwitchGroup(groupId)}>
											<ListItemIcon className="min-w-32">
												<Icon>{isActive ? 'check_circle' : 'group'}</Icon>
											</ListItemIcon>
											<ListItemText primary={group?.name ?? String(groupId)} />
										</MenuItem>
									);
								})}
							</>
						)}

						<Divider className="my-4" />
						<MenuItem
							onClick={() => {
								dispatch(logoutUser());
								userMenuClose();
							}}
						>
							<ListItemIcon className="min-w-40">
								<Icon>exit_to_app</Icon>
							</ListItemIcon>
							<ListItemText primary={t('LOGOUT')} />
						</MenuItem>
					</>
				)}
			</Popover>
		</>
	);
}

export default UserMenu;
